import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, MenuItem
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, CheckCircle, Cancel, Warning, Payment, AccountBalance } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import postingService from '../../services/posting.service';
import { useLanguage } from '../../i18n/LanguageContext';

const PurchaseInvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; id: number | null; balance: number; curr: string }>({
    open: false, id: null, balance: 0, curr: 'USD'
  });
  const [paymentForm, setPaymentForm] = useState({ amount: 0, paymentDate: new Date().toISOString().split('T')[0], paymentMethod: 'BANK_TRANSFER', reference: '' });
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try { const res = await procurementService.getPurchaseInvoices(); setInvoices(res.data); }
    catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const handlePostToLedger = async (id: number) => {
    if (!window.confirm(t('posting.post_confirm'))) return;
    try {
      const res = await postingService.postPurchaseInvoice(id);
      alert(res.data.message);
      loadInvoices();
    } catch (err: any) { alert(err.response?.data?.message || 'Error posting to ledger'); }
  };

  const handleStatusChange = async (id: number, status: string, label: string) => {
    if (!window.confirm(`${label} this bill?`)) return;
    try {
      await procurementService.updatePurchaseInvoiceStatus(id, status);
      loadInvoices();
    } catch (error: any) { alert(error.response?.data?.message || 'Error updating status'); }
  };

  const handleOpenPayment = (row: any) => {
    const balance = Number(row.totalAmount) - Number(row.paidAmount);
    setPaymentForm({ amount: balance, paymentDate: new Date().toISOString().split('T')[0], paymentMethod: 'BANK_TRANSFER', reference: '' });
    setPaymentDialog({ open: true, id: row.id, balance, curr: row.currencyCode || 'USD' });
  };

  const handleRecordPayment = async () => {
    if (!paymentDialog.id) return;
    try {
      await procurementService.recordPurchaseInvoicePayment(paymentDialog.id, paymentForm);
      setPaymentDialog({ ...paymentDialog, open: false });
      loadInvoices();
    } catch (error: any) { alert(error.response?.data?.message || 'Error recording payment'); }
  };

  const getStatusColor = (status: string): any => {
    const colors: Record<string, any> = {
      'DRAFT': 'default', 'PENDING': 'warning', 'PAID': 'success',
      'PARTIAL': 'info', 'OVERDUE': 'error', 'CANCELLED': 'default'
    };
    return colors[status] || 'default';
  };

  const columns: GridColDef[] = [
    { field: 'invoiceNumber', headerName: t('invoices.invoice_number'), width: 200 },
    { field: 'invoiceDate', headerName: t('common.date'), width: 100, renderCell: (p: GridRenderCellParams) => new Date(p.value).toLocaleDateString() },
    { field: 'dueDate', headerName: t('purchase_invoices.due_date'), width: 100, renderCell: (p: GridRenderCellParams) => new Date(p.value).toLocaleDateString() },
    { field: 'supplierName', headerName: t('purchase_orders.supplier'), flex: 1, minWidth: 150 },
    { field: 'supplierInvoiceNumber', headerName: t('purchase_invoices.vendor_ref'), width: 120 },
    { field: 'status', headerName: t('common.status'), width: 110, renderCell: (p: GridRenderCellParams) => <Chip label={p.value} color={getStatusColor(p.value as string)} size="small" /> },
    {
      field: 'totalAmount', headerName: t('common.total'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (p: GridRenderCellParams) => `${Number(p.value).toFixed(2)} ${p.row.currencyCode || 'USD'}`
    },
    {
      field: 'balanceAmount', headerName: t('common.balance'), width: 120, align: 'right', headerAlign: 'right',
      renderCell: (p: GridRenderCellParams) => {
        const val = Number(p.value); const curr = p.row.currencyCode || 'USD';
        return <span style={{ color: val > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 'bold' }}>{val.toFixed(2)} {curr}</span>;
      }
    },
    {
      field: 'actions', headerName: t('common.actions'), width: 200, sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const s = params.row.status;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {s !== 'DRAFT' && s !== 'CANCELLED' && (
              <Tooltip title={t('posting.post_to_ledger')}>
                <IconButton size="small" sx={{ color: '#6a1b9a' }} onClick={() => handlePostToLedger(params.row.id)}>
                  <AccountBalance fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'PARTIAL' || s === 'OVERDUE') && (
              <Tooltip title={t('common.record_payment')}>
                <IconButton size="small" color="success" onClick={() => handleOpenPayment(params.row)}>
                  <Payment fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'PARTIAL') && (
              <Tooltip title={t('common.mark_paid')}>
                <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={() => handleStatusChange(params.row.id, 'PAID', t('common.mark_paid'))}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {s === 'PENDING' && (
              <Tooltip title={t('common.mark_overdue')}>
                <IconButton size="small" color="warning" onClick={() => handleStatusChange(params.row.id, 'OVERDUE', t('common.mark_overdue'))}>
                  <Warning fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(s === 'PENDING' || s === 'OVERDUE') && (
              <Tooltip title={t('common.cancel')}>
                <IconButton size="small" color="error" onClick={() => handleStatusChange(params.row.id, 'CANCELLED', t('common.cancel'))}>
                  <Cancel fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('purchase_invoices.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/purchase-invoices/new')}>{t('purchase_invoices.new')}</Button>
      </Box>
      <DataGrid rows={invoices} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]} disableRowSelectionOnClick />

      {/* Payment Dialog */}
      <Dialog open={paymentDialog.open} onClose={() => setPaymentDialog({ ...paymentDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.record_payment')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>{t('common.balance')}: {paymentDialog.balance.toFixed(2)} {paymentDialog.curr}</Alert>
          <TextField fullWidth required label={t('purchase_invoices.payment_amount')} type="number" value={paymentForm.amount}
            onChange={e => setPaymentForm({ ...paymentForm, amount: Number(e.target.value) })}
            inputProps={{ min: 0.01, step: 0.01, max: paymentDialog.balance }} sx={{ mt: 1 }} />
          <TextField fullWidth required type="date" label={t('purchase_invoices.payment_date')} value={paymentForm.paymentDate}
            onChange={e => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ mt: 2 }} />
          <TextField fullWidth select label={t('purchase_invoices.payment_method')} value={paymentForm.paymentMethod}
            onChange={e => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })} sx={{ mt: 2 }}>
            <MenuItem value="BANK_TRANSFER">Bank Transfer</MenuItem>
            <MenuItem value="CASH">Cash</MenuItem>
            <MenuItem value="CHECK">Check</MenuItem>
            <MenuItem value="CREDIT_CARD">Credit Card</MenuItem>
          </TextField>
          <TextField fullWidth label={t('purchase_invoices.payment_reference')} value={paymentForm.reference}
            onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog({ ...paymentDialog, open: false })}>{t('common.cancel')}</Button>
          <Button variant="contained" color="success" onClick={handleRecordPayment}
            disabled={paymentForm.amount <= 0} startIcon={<Payment />}>{t('common.record_payment')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseInvoiceList;
